// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  BanknoteArrowUpIcon,
  CircleOffIcon,
  MessageSquareReplyIcon,
} from 'lucide-react';

import { Collapsible } from 'components/Collapsible';
import { CompletedGroupHeader } from 'components/Kit/EmailSuggestions/components/CompletedGroupHeader';
import {
  KIT_ANIMATIONS,
  SUGGESTIONS_PAGE_SIZE,
} from 'components/Kit/EmailSuggestions/constants';
import { Flex } from 'components/layout';
import { cn } from 'utils/shadcn';

import { HANDLED_STATE_EXPLANATIONS, HANDLED_STATE_LABELS } from '../constants';
import {
  HandledGroupState,
  QuoteHandledState,
  QuoteRequestBatch,
} from '../types';
import { HandledQuoteBatch } from './HandledQuoteBatch';

type HandledQuoteGroupProps = {
  state: HandledGroupState;
  batches: QuoteRequestBatch[];
  selectedIds: Set<number>;
  selectionEnabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onToggleLane: (laneId: number) => void;
  onSelectLanes: (laneIds: number[]) => void;
};

// One collapsed group (e.g. "Quoted (8)"). Shows the first page of lanes, then "Show N more".
export function HandledQuoteGroup({
  state,
  batches,
  selectedIds,
  selectionEnabled = true,
  open,
  onOpenChange,
  onToggleLane,
  onSelectLanes,
}: HandledQuoteGroupProps) {
  const [visibleLaneCount, setVisibleLaneCount] = useState(
    SUGGESTIONS_PAGE_SIZE
  );

  const laneCount = batches.reduce((sum, batch) => sum + batch.length, 0);
  const hiddenLaneCount = Math.max(laneCount - visibleLaneCount, 0);
  const visibleBatches = getVisibleBatches(batches, visibleLaneCount);

  const stateColorClasses = getHandledStateColorClasses(state);

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className='w-full'>
      <CompletedGroupHeader
        icon={getHandledStateIcon(state, stateColorClasses.icon)}
        label={HANDLED_STATE_LABELS[state]}
        count={laneCount}
        explanation={HANDLED_STATE_EXPLANATIONS[state]}
        colorClass={stateColorClasses.header}
      />
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key={`handled-group-${state}`}
            {...KIT_ANIMATIONS.toggle}
            className='mb-2 overflow-hidden'
          >
            <Flex
              direction='col'
              gap='xs'
              className={cn(
                'mt-1.5 ml-3 w-[calc(100%-12px)] border-l pl-3',
                stateColorClasses.tree
              )}
            >
              {visibleBatches.map(({ batch, visibleLanes, ordinalOffset }) => (
                <HandledQuoteBatch
                  key={batch[0].emailID}
                  visibleLanes={visibleLanes}
                  allLanes={batches.flat()}
                  state={state}
                  ordinalOffset={ordinalOffset}
                  selectedIds={selectedIds}
                  selectionEnabled={selectionEnabled}
                  onToggleLane={onToggleLane}
                  onSelectLanes={onSelectLanes}
                  showSelectAll={ordinalOffset === 0}
                />
              ))}
              {hiddenLaneCount > 0 && (
                <button
                  type='button'
                  onClick={() =>
                    setVisibleLaneCount(
                      (count: number) => count + SUGGESTIONS_PAGE_SIZE
                    )
                  }
                  className='w-fit border-none bg-transparent px-1 text-[10px] text-accent-600 underline cursor-pointer'
                >
                  {`Show ${hiddenLaneCount} more`}
                </button>
              )}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </Collapsible>
  );
}

function getHandledStateColorClasses(state: HandledGroupState): {
  header: string;
  icon: string;
  tree: string;
} {
  switch (state) {
    case QuoteHandledState.Replied:
      return {
        header: 'border-success-300 bg-success-50 hover:bg-success-100',
        icon: 'h-3.5 w-3.5 shrink-0 text-success-700',
        tree: 'border-success-300',
      };
    case QuoteHandledState.Quoted:
      return {
        header: 'border-warning-400 bg-warning-100 hover:bg-warning-200',
        icon: 'h-3.5 w-3.5 shrink-0 text-warning-700',
        tree: 'border-warning-400',
      };
    case QuoteHandledState.Skipped:
      return {
        header: 'border-neutral-300 bg-neutral-50 hover:bg-neutral-100',
        icon: 'h-3.5 w-3.5 shrink-0 text-neutral-600',
        tree: 'border-neutral-300',
      };
  }
}

function getHandledStateIcon(state: HandledGroupState, className: string) {
  switch (state) {
    case QuoteHandledState.Replied:
      return <MessageSquareReplyIcon className={className} />;
    case QuoteHandledState.Quoted:
      return <BanknoteArrowUpIcon className={className} />;
    case QuoteHandledState.Skipped:
      return <CircleOffIcon className={className} />;
  }
}

type VisibleBatch = {
  batch: QuoteRequestBatch;
  visibleLanes: QuoteRequestBatch;
  ordinalOffset: number;
};

// First `limit` lanes across batches, keeping the batch grouping.
function getVisibleBatches(
  batches: QuoteRequestBatch[],
  limit: number
): VisibleBatch[] {
  const visible: VisibleBatch[] = [];
  let remaining = limit;
  let ordinalOffset = 0;
  for (const batch of batches) {
    if (remaining <= 0) {
      break;
    }
    visible.push({
      batch,
      visibleLanes: batch.slice(0, remaining),
      ordinalOffset,
    });
    remaining -= batch.length;
    ordinalOffset += batch.length;
  }
  return visible;
}
